import { type ReactNode } from 'react'

import { ContentLayout, ContentMain } from '@/components/ContentLayout'
import { PayoutsAdminHeader } from '@/features/payouts/admin/components/PayoutsAdminHeader'
import { Sandwich, SandwichBun, SandwichContent } from '@/components/Sandwich'
import { ListHeader } from '@/components/ListHeader'
import { ContentError } from '@/components/ContentError'
import {
  SideSheet,
  SideSheetHostView,
  SideSheetMain,
} from '@/components/SideSheet'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { Button } from '@/components/Button'

export interface PayoutReviewProps {
  canAccess: boolean
  sheetVisible: boolean
  actionsDisabled: boolean
  onNewTransaction: () => unknown
  pendingTransactionsCount: number
  pendingTransactionsTable: ReactNode
  draftingView: ReactNode
  dialogs?: ReactNode
}

export function PayoutReview({
  canAccess,
  sheetVisible,
  actionsDisabled,
  onNewTransaction,
  pendingTransactionsCount,
  pendingTransactionsTable,
  draftingView,
  dialogs,
}: PayoutReviewProps) {
  const { connectRef: scrollRef, scrollYStart: scrollOffset } =
    useScrollOffsets()

  if (!canAccess) {
    return (
      <ContentError
        title="Denied"
        size="lg"
        error="You are not allowed to access this page."
      />
    )
  }

  return (
    <>
      <SideSheetHostView sheetVisible={sheetVisible}>
        <SideSheetMain>
          <Sandwich stretch>
            <SandwichBun>
              <PayoutsAdminHeader scrollOffset={scrollOffset}>
                <Button
                  label="New Transaction"
                  variant="secondary"
                  isDisabled={actionsDisabled}
                  iconLeft="add"
                  onPress={onNewTransaction}
                />
              </PayoutsAdminHeader>
            </SandwichBun>
            <SandwichContent stretch>
              <ContentLayout
                accountForLabel
                accountForTabBar
                fill
                ref={scrollRef}
              >
                <ContentMain fill constrainWidth>
                  <Sandwich stretch>
                    <SandwichBun>
                      <ListHeader count={pendingTransactionsCount || undefined}>
                        Pending transactions
                      </ListHeader>
                    </SandwichBun>
                    <SandwichContent stretch>
                      {pendingTransactionsTable}
                    </SandwichContent>
                  </Sandwich>
                </ContentMain>
              </ContentLayout>
            </SandwichContent>
          </Sandwich>
        </SideSheetMain>

        <SideSheet>{draftingView}</SideSheet>
      </SideSheetHostView>
      {dialogs}
    </>
  )
}
